<template>
  <Authenticator
    hideSignUp
    :sign-up-attributes="['email', 'name', 'address']"
    :form-fields="formFields"
  >
    <slot v-if="canRenderAppSlot" />
  </Authenticator>
</template>
<script setup>
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-vue";
import { inject, watch, computed, ref } from "vue";

const { notifyError, notifySuccess } = inject("notifications");
const auth = useAuthenticator();

// Import Vuex store
import { useStore } from "vuex";
import { userSession as userSessionNamespace } from "@/store/namespaces.js";

const store = useStore();
const userSessionStarted = ref(false);

const formFields = {
  signUp: {
    email: {
      order: 1,
      isRequired: true,
    },
    name: {
      order: 2,
      label: "Full name",
      placeholder: "Jane Doe",
      isRequired: true,
    },
    address: {
      order: 3,
      label: "Address",
      placeholder: "123 Main St, City, State ZIP",
      isRequired: true,
    },
  },
};

// When user successfully logs in (using Amplify Auth), we create a user session in the backend
const startUserSession = async () => {
  try {
    await store.dispatch(`${userSessionNamespace}/startUserSession`);
    await store.dispatch(`${userSessionNamespace}/initIntervalsAndTimeouts`);
    notifySuccess("User session started successfully.");
  } catch (error) {
    notifyError("Unable to start user session. Signing out.");
  }
};

const userInfo = computed(
  () => store.getters[`${userSessionNamespace}/getUserInfo`]
);

const canRenderAppSlot = computed(() => {
  if (auth.authStatus !== "authenticated") {
    return false;
  }

  return Boolean(userInfo.value);
});

const ensureAuthenticatedUserContext = async () => {
  console.log("Ensuring authenticated user context...");
  if (auth.authStatus !== "authenticated") {
    return;
  }

  if (!userSessionStarted.value) {
    await startUserSession();
    userSessionStarted.value = true;
  }

  if (!userInfo.value) {
    await store.dispatch(`${userSessionNamespace}/getUserInfo`);
  }
};

// Watch for authentication state changes
watch(
  () => auth.authStatus,
  async (newStatus, oldStatus) => {
    console.log("Auth status changed from ", oldStatus, " to ", newStatus);
    if (newStatus === "authenticated") {
      await ensureAuthenticatedUserContext();
      return;
    }

    if (newStatus === "unauthenticated") {
      console.log("User is unauthenticated. Resetting user session state.");
      userSessionStarted.value = false;
    }
  },
  { immediate: true }
);
</script>
